import { useState } from "react";
import type { StoreThemeJson } from "@a2z/storefront-schemas";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { ThemeDraftApi } from "../state/useThemeDraft";

/**
 * The raw-JSON escape hatch, folded into a disclosure below the visual editor.
 *
 * **Why this survives the rewrite.** The visual inspector covers section
 * settings, but a `StoreThemeJson` is more than that: theme TOKENS
 * (`settings` — palette, type, button variants), custom `pages`, block
 * instances inside sections, and the `$category:N` / `$products:N` slot
 * vocabulary that a preset template uses to bind store data. None of those have
 * a visual control yet. Deleting the textarea would have made the studio
 * strictly less capable than the thing it replaced — a regression dressed as an
 * upgrade.
 *
 * It writes through `draft.replaceDraft`, the same single writer the visual
 * panes use, so the two editing modes cannot disagree about what the document
 * is. Applying does NOT save — the toolbar's Save is still the only thing that
 * persists, so a malformed paste is recoverable by reloading.
 */
export function RawJsonPanel({ draft }: { draft: ThemeDraftApi }) {
  const t = useTranslations();
  const [isOpen, setIsOpen] = useState(false);
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);

  const open = () => {
    // Seed from the CURRENT draft each time it is opened, so the textarea can
    // never show a stale document that a paste would then silently revert to.
    setText(JSON.stringify(draft.draft, null, 2));
    setError(null);
    setIsOpen(true);
  };

  const apply = () => {
    setError(null);
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      setError(t(keys.themes.jsonParseError));
      return;
    }
    // Structural gate before it reaches the draft. An empty `templates` is a
    // technically valid but WIPED theme that the backend validator accepts, so
    // a structure mistake has to fail loudly here rather than sail through and
    // blank the page.
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      Array.isArray(parsed) ||
      typeof (parsed as Record<string, unknown>).settings !== "object" ||
      typeof (parsed as Record<string, unknown>).templates !== "object"
    ) {
      setError(t(keys.themes.jsonParseError));
      return;
    }
    draft.replaceDraft(parsed as StoreThemeJson);
    setIsOpen(false);
  };

  if (!isOpen) {
    return (
      <button
        type="button"
        onClick={open}
        className="self-start rounded-full border border-[color:var(--border-light)] px-3 py-1.5 text-[11.5px] font-semibold text-[color:var(--text-primary)] hover:border-[color:var(--primary-500)]"
      >
        {t(keys.themes.studioOpenJson)}
      </button>
    );
  }

  return (
    <div className="rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[12px] font-bold text-[color:var(--text-primary)]">
          {t(keys.themes.jsonEditor)}
        </span>
        <button
          type="button"
          onClick={() => setIsOpen(false)}
          className="text-[11.5px] font-semibold text-[color:var(--text-muted)]"
        >
          {t(keys.common.close)}
        </button>
      </div>
      <p className="mb-2 text-[11px] text-[color:var(--text-muted)]">
        {t(keys.themes.studioJsonHelper)}
      </p>
      <textarea
        dir="ltr"
        rows={18}
        spellCheck={false}
        value={text}
        onChange={(e) => setText(e.target.value)}
        className="w-full rounded-lg border border-[color:var(--border-light)] bg-[color:var(--surface)] p-2.5 font-mono text-[11.5px] text-[color:var(--text-primary)] outline-none focus:border-[color:var(--primary-500)]"
      />
      {error ? (
        <p className="mt-1.5 text-[11.5px] text-[color:var(--error-600)]">
          {error}
        </p>
      ) : null}
      <button
        type="button"
        onClick={apply}
        className="mt-2 rounded-full bg-[color:var(--primary-500)] px-4 py-1.5 text-[11.5px] font-semibold text-white"
      >
        {t(keys.themes.studioApplyJson)}
      </button>
    </div>
  );
}
